import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, PenLine, Settings2, X } from 'lucide-react';
import { Button } from '@kop/ui';

import { BoardDetailPanel } from '../briefing/board-detail-panel';
import { FeatureLoading } from '../feature-skeleton';
import { getTrustedMediaAdapter, type TrustedMediaStatus } from '../media/trusted-media-adapter';
import { SafetyBoardRows } from './board-rows';
import {
  newBoardId,
  boardEmphasisLabel,
  displayValuesCommand,
  orderedPosts,
  postCommand,
  readerAuthority,
  type BoardAttachment,
  type BoardEmphasis,
  type SafetyBoardAuthority,
  type SafetyBoardCommand,
  type SafetyBoardPost,
  type SafetyBoardSettings,
} from './model';
import { createSafetyBoardRepository, safetyBoardFailureMessage } from './repository';
import { SafetyBoardPolicyDialog } from './safety-board-policy-admin';
import { useSafetyBoardPage } from './use-safety-board-page';
import { useSafetyBoardSite } from './site-context';

import './safety-board.css';

const PAGE_ROWS = 50;
const ARCHIVE_PAGE_SIZE = 10;
const TITLE_LIMIT = 160;
const BODY_LIMIT = 4000;

type AttachmentDraft = BoardAttachment &
  Readonly<{ fileName: string; previewUrl?: string; existing: boolean }>;
type Draft = Readonly<{
  boardId: string;
  expectedVersion: number;
  category: string;
  title: string;
  body: string;
  attachments: readonly AttachmentDraft[];
  emphasis: BoardEmphasis;
  pinned: boolean;
  displayVersion: number;
}>;

const draftOf = (post: SafetyBoardPost): Draft => ({
  boardId: post.boardId,
  expectedVersion: post.version,
  category: post.category,
  title: post.title,
  body: post.body,
  attachments: post.attachments.map((item, index) => ({
    ...item,
    fileName: `기존 이미지 ${index + 1}`,
    existing: true,
  })),
  emphasis: post.emphasis,
  pinned: post.pinned,
  displayVersion: post.displayVersion,
});

const emphasisColor = {
  normal: 'default',
  important: 'warning',
  urgent: 'critical',
} as const;

export function SafetyBoardWorkspace({
  owner,
  onClose,
}: {
  owner: { uid: string; contractorId: string | null };
  onClose(): void;
}) {
  const { snapshot, reload, acceptReadReceipt } = useSafetyBoardPage(PAGE_ROWS);
  const siteId = useSafetyBoardSite();
  const [settings, setSettings] = useState<SafetyBoardSettings | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [attachmentStatus, setAttachmentStatus] = useState<TrustedMediaStatus | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [archivePage, setArchivePage] = useState(0);

  useEffect(() => {
    if (snapshot.state === 'ready' && snapshot.page.settings) setSettings(snapshot.page.settings);
  }, [snapshot]);

  const page = snapshot.state === 'ready' ? snapshot.page : null;
  const projectedAuthority: SafetyBoardAuthority = settings?.authority.canManage
    ? { publisher: true, canCompose: true, canRelease: true }
    : readerAuthority;
  const selected = page?.items.find((item) => item.boardId === selectedId) ?? null;
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('ko-KR');
    return orderedPosts(page?.items ?? []).filter((post) => {
      const date = (post.publishedAt ?? post.updatedAt).slice(0, 10);
      return (
        (category === 'all' || post.category === category) &&
        (!normalized ||
          `${post.title} ${post.body}`.toLocaleLowerCase('ko-KR').includes(normalized)) &&
        (!dateFrom || date >= dateFrom) &&
        (!dateTo || date <= dateTo)
      );
    });
  }, [category, dateFrom, dateTo, page?.items, query]);
  const archivePageCount = Math.max(1, Math.ceil(filtered.length / ARCHIVE_PAGE_SIZE));
  const visiblePosts = filtered.slice(
    archivePage * ARCHIVE_PAGE_SIZE,
    (archivePage + 1) * ARCHIVE_PAGE_SIZE,
  );
  useEffect(() => {
    if (archivePage >= archivePageCount) setArchivePage(archivePageCount - 1);
  }, [archivePage, archivePageCount]);
  const runCommand = useCallback(
    async (command: SafetyBoardCommand) => {
      await (await createSafetyBoardRepository(siteId)).execute(command);
      await reload();
    },
    [reload, siteId],
  );

  const attachImage = async (file: File) => {
    if (!draft || !settings || draft.attachments.length >= settings.maxImages) return;
    setBusy(true);
    setFailure('');
    try {
      const media = getTrustedMediaAdapter();
      if (!media.enabled) throw new Error('이미지 첨부 기능을 현재 사용할 수 없습니다.');
      const reference = await media.upload(
        file,
        {
          fileName: file.name || '게시판 이미지.png',
          evidenceId: draft.boardId,
          purpose: 'communications-evidence',
          evidenceClass: 'corrective_action',
          ownerUid: owner.uid,
          scope: { tenantId: 'kop', siteId: settings.siteId, contractorId: owner.contractorId },
        },
        setAttachmentStatus,
      );
      const hydrated = await media.hydrate('kop', reference);
      if (!hydrated) throw new Error('검증된 이미지 업로드 영수증이 필요합니다.');
      setDraft((current) =>
        current
          ? {
              ...current,
              attachments: [
                ...current.attachments,
                {
                  manifestId: reference.manifestId,
                  sha256: reference.evidenceVariantSha256,
                  fileName: file.name || '게시판 이미지',
                  previewUrl: URL.createObjectURL(file),
                  existing: false,
                },
              ],
            }
          : current,
      );
    } catch (error) {
      setFailure(error instanceof Error ? error.message : '이미지를 첨부하지 못했습니다.');
    } finally {
      setBusy(false);
      setAttachmentStatus(null);
    }
  };

  const closeDraft = () => {
    for (const item of draft?.attachments ?? [])
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    setDraft(null);
  };
  const submit = async () => {
    if (!draft) return;
    setBusy(true);
    setFailure('');
    try {
      const repository = await createSafetyBoardRepository(siteId);
      await repository.execute(
        postCommand({
          boardId: draft.boardId,
          expectedVersion: draft.expectedVersion,
          category: draft.category,
          title: draft.title.trim(),
          body: draft.body.trim(),
          attachments: draft.attachments.map(({ manifestId, sha256 }) => ({ manifestId, sha256 })),
        }),
      );
      await repository.execute(
        displayValuesCommand({
          boardId: draft.boardId,
          expectedVersion: draft.displayVersion,
          pinned: draft.pinned,
          titleColor: emphasisColor[draft.emphasis],
        }),
      );
      await reload();
      closeDraft();
    } catch (error) {
      setFailure(safetyBoardFailureMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="safety-board-archive-layer" onMouseDown={onClose}>
      <section
        className="safety-board-archive glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-board-archive-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="safety-board-archive-head">
          <h2 id="safety-board-archive-title">안전보건게시판</h2>
          <div>
            {settings?.authority.canManage && (
              <>
                <Button
                  variant="quiet"
                  size="compact"
                  icon={<Settings2 size={16} />}
                  aria-label="게시판 설정"
                  title="게시판 설정"
                  onClick={() => setPolicyOpen(true)}
                />
                <Button
                  size="compact"
                  icon={<PenLine size={15} />}
                  onClick={() => {
                    if (!settings.configuration.ready) {
                      setPolicyOpen(true);
                      return;
                    }
                    setDraft({
                      boardId: newBoardId(),
                      expectedVersion: 0,
                      category: settings.categories[0]!.id,
                      title: '',
                      body: '',
                      attachments: [],
                      emphasis: 'normal',
                      pinned: false,
                      displayVersion: 0,
                    });
                  }}
                >
                  글쓰기
                </Button>
              </>
            )}
            <button
              type="button"
              className="glass-icon-button"
              aria-label="게시판 닫기"
              onClick={onClose}
            >
              <X size={19} />
            </button>
          </div>
        </header>
        {failure && (
          <p className="safety-board-feedback" role="alert">
            {failure}
          </p>
        )}
        {!settings && snapshot.state !== 'loading' && (
          <p className="safety-board-feedback" role="status">
            게시판을 사용할 수 없습니다.
          </p>
        )}
        <div className="safety-board-archive-tools">
          <input
            type="search"
            aria-label="게시글 검색"
            placeholder="검색"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setArchivePage(0);
            }}
          />
          <select
            aria-label="분류"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setArchivePage(0);
            }}
          >
            <option value="all">전체 분류</option>
            {settings?.categories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            aria-label="시작일"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setArchivePage(0);
            }}
          />
          <input
            type="date"
            aria-label="종료일"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setArchivePage(0);
            }}
          />
        </div>
        <div className="safety-board-archive-body">
          {snapshot.state === 'loading' ? (
            <FeatureLoading label="게시판 불러오는 중" compact />
          ) : snapshot.state === 'unavailable' ? (
            <p className="safety-board-empty">게시판을 불러오지 못했습니다.</p>
          ) : filtered.length === 0 ? (
            <p className="safety-board-empty">게시글이 없습니다.</p>
          ) : (
            <>
              <SafetyBoardRows
                items={visiblePosts}
                categories={settings?.categories ?? []}
                onOpen={(post) => setSelectedId(post.boardId)}
              />
              <nav className="safety-board-pagination" aria-label="게시글 페이지">
                <Button
                  variant="quiet"
                  size="compact"
                  disabled={archivePage === 0}
                  onClick={() => setArchivePage((value) => Math.max(0, value - 1))}
                >
                  이전
                </Button>
                <span>
                  {archivePage + 1}/{archivePageCount}
                </span>
                <Button
                  variant="quiet"
                  size="compact"
                  disabled={archivePage + 1 >= archivePageCount}
                  onClick={() =>
                    setArchivePage((value) => Math.min(archivePageCount - 1, value + 1))
                  }
                >
                  다음
                </Button>
              </nav>
            </>
          )}
        </div>

        {draft && settings && (
          <div className="safety-board-editor-layer">
            <section className="safety-board-editor glass-surface" aria-label="게시글 작성">
              <header>
                <h3>{draft.expectedVersion ? '게시글 수정' : '새 게시글'}</h3>
                <button
                  type="button"
                  className="glass-icon-button"
                  aria-label="작성 닫기"
                  onClick={closeDraft}
                >
                  <X size={18} />
                </button>
              </header>
              <label>
                분류
                <select
                  value={draft.category}
                  disabled={busy}
                  onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                >
                  {settings.categories.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="safety-board-priority">
                <legend>중요도</legend>
                {(['normal', 'important', 'urgent'] as const).map((value) => (
                  <Button
                    key={value}
                    type="button"
                    variant={draft.emphasis === value ? 'secondary' : 'quiet'}
                    size="compact"
                    aria-pressed={draft.emphasis === value}
                    disabled={busy}
                    onClick={() => setDraft({ ...draft, emphasis: value })}
                  >
                    {boardEmphasisLabel[value]}
                  </Button>
                ))}
              </fieldset>
              <label>
                제목
                <input
                  value={draft.title}
                  maxLength={TITLE_LIMIT}
                  disabled={busy}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                />
              </label>
              <label>
                내용
                <textarea
                  value={draft.body}
                  maxLength={BODY_LIMIT}
                  rows={10}
                  disabled={busy}
                  onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                />
              </label>
              <div className="safety-board-image-editor">
                <label className="safety-board-image-add">
                  <ImagePlus size={15} /> 이미지 추가
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={busy || draft.attachments.length >= settings.maxImages}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      event.currentTarget.value = '';
                      if (file) void attachImage(file);
                    }}
                  />
                </label>
                <span>
                  {attachmentStatus?.state === 'uploading'
                    ? `업로드 ${Math.round(attachmentStatus.progress * 100)}%`
                    : `${draft.attachments.length}/${settings.maxImages}`}
                </span>
              </div>
              {draft.attachments.length > 0 && (
                <ul className="safety-board-attachments">
                  {draft.attachments.map((item) => (
                    <li key={item.manifestId}>
                      {item.previewUrl ? (
                        <img src={item.previewUrl} alt="" />
                      ) : (
                        <span className="safety-board-existing-image">기존 이미지</span>
                      )}
                      <span>{item.fileName}</span>
                      <button
                        type="button"
                        aria-label={`${item.fileName} 삭제`}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            attachments: draft.attachments.filter(
                              (candidate) => candidate.manifestId !== item.manifestId,
                            ),
                          })
                        }
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <footer>
                <Button variant="secondary" size="compact" disabled={busy} onClick={closeDraft}>
                  취소
                </Button>
                <Button
                  size="compact"
                  disabled={busy || !draft.title.trim() || !draft.body.trim()}
                  onClick={() => void submit()}
                >
                  게시
                </Button>
              </footer>
            </section>
          </div>
        )}
        {policyOpen && settings?.authority.canManage && (
          <SafetyBoardPolicyDialog
            initialSettings={settings}
            onClose={() => setPolicyOpen(false)}
            onSaved={setSettings}
          />
        )}
      </section>
      {selected && (
        <BoardDetailPanel
          post={selected}
          settings={settings}
          authority={projectedAuthority}
          onClose={() => setSelectedId(null)}
          onReadReceipt={acceptReadReceipt}
          onCommand={runCommand}
          onEdit={(post) => {
            setSelectedId(null);
            setDraft(draftOf(post));
          }}
        />
      )}
    </div>,
    document.querySelector<HTMLElement>('.workspace-shell') ?? document.body,
  );
}
